import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LoginIcon from "@mui/icons-material/Login";
import { useTranslation } from "react-i18next";

interface FriendPoolEntry {
  id: number;
  name: string;
}

interface Props {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  names: FriendPoolEntry[];
  onNamesChange: (names: FriendPoolEntry[]) => void;
  playerNames: string[];
  isHost: boolean;
  isAuthenticated: boolean;
  onLoginClick: () => void;
}

let nextTempId = -1;

export default function FriendPoolEditor({
  enabled,
  onEnabledChange,
  names,
  onNamesChange,
  playerNames,
  isHost,
  isAuthenticated,
  onLoginClick,
}: Props) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState("");

  const handleAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (names.some((n) => n.name.toLowerCase() === trimmed.toLowerCase())) return;
    onNamesChange([...names, { id: nextTempId--, name: trimmed }]);
    setNewName("");
  };

  const handleRemove = (entry: FriendPoolEntry) => {
    onNamesChange(names.filter((n) => n.id !== entry.id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  // Custom names = names that don't match any current player
  const customNames = names.filter(
    (n) => !playerNames.some((pn) => pn.toLowerCase() === n.name.toLowerCase())
  );

  return (
    <Box sx={{ bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2, p: 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t("friend_pool_title")}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={enabled}
              onChange={(_, v) => isHost && onEnabledChange(v)}
              disabled={!isHost}
            />
          }
          label=""
          sx={{ m: 0 }}
        />
      </Stack>

      {enabled && (
        <Stack spacing={1} sx={{ mt: 0.75 }}>
          {/* Players (always shown, auto-included) */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              {t("friend_pool_players")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
              {playerNames.map((pn) => (
                <Chip
                  key={`player-${pn}`}
                  label={pn}
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ fontSize: "0.75rem" }}
                />
              ))}
            </Box>
          </Box>

          {/* Custom friend names */}
          {customNames.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                {t("friend_pool_extra")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
                {customNames.map((entry) => (
                  <Chip
                    key={entry.id}
                    label={entry.name}
                    size="small"
                    variant="outlined"
                    onDelete={isHost ? () => handleRemove(entry) : undefined}
                    sx={{ fontSize: "0.75rem" }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Add custom name */}
          {isHost && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                size="small"
                placeholder={t("friend_pool_add_placeholder")}
                value={newName}
                onChange={(e) => setNewName(e.target.value.slice(0, 30))}
                onKeyDown={handleKeyDown}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" },
                }}
              />
              <IconButton size="small" onClick={handleAdd} disabled={!newName.trim()}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}

          {/* Login hint for saving */}
          {isHost && !isAuthenticated && (
            <Button
              size="small"
              variant="text"
              startIcon={<LoginIcon />}
              onClick={onLoginClick}
              sx={{ fontSize: "0.7rem", textTransform: "none", justifyContent: "flex-start" }}
            >
              {t("friend_pool_login_hint")}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
